import React from 'react';

interface RadioProps {
  value: string;
  label: string;
  disabled?: boolean;
}

interface RadioGroupProps {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const RadioContext = React.createContext<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}>({
  value: '',
  onChange: () => {},
  disabled: false
});

export const RadioGroup: React.FC<RadioGroupProps> = ({ 
  value, 
  onChange, 
  children, 
  disabled = false,
  className = '' 
}) => {
  return (
    <RadioContext.Provider value={{ value, onChange, disabled }}>
      <div className={`space-y-2 ${className}`} role="radiogroup">
        {children}
      </div>
    </RadioContext.Provider>
  );
};

export const Radio: React.FC<RadioProps> = ({ 
  value, 
  label, 
  disabled = false 
}) => {
  const context = React.useContext(RadioContext);
  const isSelected = context.value === value;
  const isDisabled = disabled || context.disabled;

  const handleClick = () => {
    if (!isDisabled && !isSelected) {
      context.onChange(value);
    }
  };

  return (
    <label 
      className={`
        flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-300
        ${isDisabled 
          ? 'opacity-50 cursor-not-allowed' 
          : 'hover:bg-gray-800/50'
        }
        ${isSelected 
          ? 'bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border border-cyan-500/50 shadow-lg shadow-cyan-500/10' 
          : 'bg-gray-900/50 border border-gray-700'
        }
      `}
      onClick={handleClick}
    >
      <div 
        className={`
          w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300
          ${isSelected 
            ? 'border-cyan-400 bg-cyan-400' 
            : 'border-gray-500 bg-transparent'
          }
        `}
      >
        {isSelected && (
          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
        )}
      </div>
      <span className={`font-medium ${isSelected ? 'text-cyan-300' : 'text-gray-300'}`}>
        {label}
      </span>
      {isSelected && (
        <span className="ml-auto text-xs text-cyan-400 font-mono">ACTIVE</span>
      )}
    </label>
  );
};

export default Radio;
